import { memo } from 'react'
import ReactMarkdown from 'react-markdown'
import type { PluggableList } from 'unified'
import { useTranslation } from '@/i18n'
import {
  markdownComponents,
  rehypePlugins as rehypePluginsConfig,
  remarkPlugins as remarkPluginsConfig,
} from '@/config/markdownConfig'
import type { HelpAgentMessage } from '@/store/helpAgentSlice'

// Same cast the main chat's MessageBubble applies — the config arrays mix
// plugin/option tuple shapes that unified's Pluggable union can't infer.
const remarkPlugins = remarkPluginsConfig as PluggableList
const rehypePlugins = rehypePluginsConfig as PluggableList

/**
 * One chat bubble. User: dark pill, end-aligned. Assistant: light gray,
 * start-aligned, markdown-rendered (numbered how-to steps come from the
 * shared markdown config so styling matches the main chat).
 */
const HelpAgentMessageBubble = ({ message }: { message: HelpAgentMessage }) => {
  const { t } = useTranslation('help_agent')

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap rounded-lg bg-gray-900 px-3 py-2 text-sm text-white dark:bg-black">
          {message.content}
        </div>
      </div>
    )
  }

  if (message.status === 'error' && message.content === '') {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
          {t('message_error')}
        </div>
      </div>
    )
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-900 dark:bg-dark-accent dark:text-dark-text [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        <ReactMarkdown
          components={markdownComponents}
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
        >
          {message.content}
        </ReactMarkdown>
      </div>
    </div>
  )
}

// memo: the slice's rAF flush replaces the messages array each frame while
// streaming; unpatched messages keep object identity, so memo confines
// per-frame markdown re-parsing to the one streaming bubble.
export default memo(HelpAgentMessageBubble)
